import { useCallback, useEffect } from 'react';
import {
  useBorrowerProcessNotifications,
  BorrowerProcessProgressDto
} from 'shared/signalR/hooks/useBorrowerProcessNotifications';
import { toasterService } from 'components/feedback/Toaster';
import { useBorrowerGlobalInfos } from '../store';
import { useSubscriptionProcessActions } from 'features/subscription-process/contexts/SubscriptionProcessContext';
import { useProcessedNotificationsGeneric } from './useProcessedNotifications';

interface UseBorrowerProcessSyncOptions {
  /** Called when process is updated */
  onProcessUpdated?: () => void;
  /** Show toast notifications (default: true) */
  showToasts?: boolean;
}

/**
 * Hook centralisant la synchronisation du processus emprunteur :
 * - Connexion SignalR pour les notifications temps réel du processus
 * - Refresh automatique via refreshSubscriptionProcess
 * - Affichage des toasts de notification
 *
 * @example
 * ```tsx
 * const { isConnected, refresh } = useBorrowerProcessSync();
 * ```
 */
export const useBorrowerProcessSync = (
  options: UseBorrowerProcessSyncOptions = {}
) => {
  const { onProcessUpdated, showToasts = true } = options;
  const globalInfos = useBorrowerGlobalInfos();
  const { refreshSubscriptionProcess } = useSubscriptionProcessActions();

  const { memoizedConnect, memoizedDisconnect, notifications, isConnected } =
    useBorrowerProcessNotifications(globalInfos?.quoteId);

  // Connexion SignalR
  useEffect(() => {
    console.log('[BorrowerProcessSync] Connecting SignalR for quoteId:', globalInfos?.quoteId);
    memoizedConnect();
    return () => {
      console.log('[BorrowerProcessSync] Disconnecting SignalR');
      memoizedDisconnect();
    };
  }, [memoizedConnect, memoizedDisconnect, globalInfos?.quoteId]);

  // Handler pour les nouvelles notifications
  const handleNewNotification = useCallback(
    (notification: BorrowerProcessProgressDto) => {
      console.log('[BorrowerProcessSync] New notification received:', notification);
      console.log('[BorrowerProcessSync] PopIn:', notification.notificationPopIn);

      // Afficher le toast si configuré
      if (showToasts && notification.notificationPopIn) {
        console.log('[BorrowerProcessSync] Showing toast:', notification.notificationPopIn.title);
        toasterService.showToast({
          title: notification.notificationPopIn.title,
          subtitle: notification.notificationPopIn.subtitle,
          message: notification.notificationPopIn.message,
          displayType: notification.notificationPopIn.displayType,
          color: notification.notificationPopIn.color
        });
      }

      // Rafraîchir les données via le context centralisé
      console.log('[BorrowerProcessSync] Refreshing subscription process...');
      refreshSubscriptionProcess();

      // Appel du callback
      onProcessUpdated?.();
    },
    [refreshSubscriptionProcess, onProcessUpdated, showToasts]
  );

  // Utilisation du hook centralisé pour la déduplication
  useProcessedNotificationsGeneric(notifications, handleNewNotification);

  return {
    /** Whether SignalR is connected */
    isConnected,
    /** Manually refresh process data */
    refresh: useCallback(() => {
      refreshSubscriptionProcess();
    }, [refreshSubscriptionProcess])
  };
};
